import { debounce } from "lodash";
import moment from "moment";
import scrollTo from "PFCore/helpers/scroll_to";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { MutableRefObject, UIEvent, useCallback, useState } from "react";

import { useCalendarContext } from "../overview/context/calendar_context";
import { useCalendarGridScope } from "./use_calendar_grid_scope";

interface UseCalendarScrollingProps {
  calendarScrollWrapperRef: MutableRefObject<HTMLDivElement | null>;
  calendarHeaderRef: MutableRefObject<HTMLDivElement | null>;
}

export type CalendarScrolling = {
  widthScrolled: number;
  scrollToDate: (date: string | null) => void;
  scrollToLeftOffset: (value: number, animate: boolean, debounced: boolean) => void;
  handleCalendarViewScroll: (e: UIEvent<HTMLDivElement>) => void;
};

const getScrollValue = (value, scrollWidth, clientWidth) => {
  if (value < 0) {
    return 0;
  } else if (value > scrollWidth - clientWidth) {
    return scrollWidth - clientWidth;
  } else {
    return value;
  }
};

export const useCalendarScrolling = ({
  calendarScrollWrapperRef,
  calendarHeaderRef
}: UseCalendarScrollingProps): CalendarScrolling => {
  const { dayWidth } = useCalendarContext();
  const calendarGridScope = useCalendarGridScope();

  const { utc } = useDateFormatter();

  const [widthScrolled, setWidthScrolled] = useState<number>(0);
  const debouncedSetWidthScrolled = debounce((value: number) => setWidthScrolled(value), 200);

  const scrollToLeftOffset = (value: number, animate = false, debounced = false) => {
    const setScrolledLeftOffset = debounced ? debouncedSetWidthScrolled : setWidthScrolled;
    if (!calendarScrollWrapperRef.current || !calendarHeaderRef.current) {
      return;
    }
    const { clientWidth, scrollWidth } = calendarScrollWrapperRef.current;
    const scrollValue = getScrollValue(value, scrollWidth, clientWidth);
    if (animate) {
      scrollTo(calendarScrollWrapperRef.current, scrollValue, 0);
      scrollTo(calendarHeaderRef.current, scrollValue, 0);
    } else {
      calendarScrollWrapperRef.current.scrollLeft = scrollValue;
      calendarHeaderRef.current.scrollLeft = scrollValue;
    }
    setScrolledLeftOffset(scrollValue);
  };

  const handleCalendarViewScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!calendarScrollWrapperRef.current || !calendarHeaderRef.current) {
      return;
    }
    if (e.currentTarget.scrollLeft !== calendarScrollWrapperRef.current.scrollLeft) {
      calendarScrollWrapperRef.current.scrollTo(e.currentTarget.scrollLeft, 0);
    }
    if (e.currentTarget.scrollLeft !== calendarHeaderRef.current.scrollLeft) {
      calendarHeaderRef.current.scrollTo(e.currentTarget.scrollLeft, 0);
    }
    debouncedSetWidthScrolled(e.currentTarget.scrollLeft);
  };

  const scrollToDate = useCallback(
    (date) => {
      if (!date) {
        return;
      }
      const daysFromStart = moment.duration(utc(date).diff(utc(calendarGridScope.min))).asDays();
      scrollToLeftOffset(daysFromStart * dayWidth, true);
    },
    [calendarGridScope, scrollToLeftOffset]
  );

  return {
    widthScrolled,
    scrollToDate,
    scrollToLeftOffset,
    handleCalendarViewScroll
  };
};
